export interface Integration {
  id: string;
  name: string;
  status: "CONNECTED" | "DISCONNECTED";
  last_synced_at: string | null;
  service_credential_name: string;
  service_credential_type: string;
  service_credential_id: string;
  created_at: string;
  updated_at: string;
}

export interface Connection {
  id: string;
  account_id: string;
  name: string;
  service: string;
  configuration?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ServiceCatalogItem {
  name: string;
  config_type: string;
}

export interface ServiceCatalog {
  [key: string]: ServiceCatalogItem;
}

interface ConnectResponse {
  redirect_url: string;
  error?: string;
}

interface DisconnectResponse {
  message?: string;
  error?: string;
}

interface CompleteConnectResponse {
  success: boolean;
  error?: string;
}

interface IntegrationsResponse {
  connections: Integration[];
}

interface CreateConnectionRequest {
  name: string;
  service: string;
  configuration?: Record<string, unknown>;
}

interface UpdateConnectionRequest {
  name?: string;
  service?: string;
  configuration?: Record<string, unknown>;
}

interface OAuth2ConnectRequest extends Record<string, unknown> {
  service_name: string;
  integration_id: string;
}

interface OAuth2ConnectResponse {
  authorization_url: string;
  state: string;
}

interface OAuth2CallbackRequest extends Record<string, unknown> {
  service_name: string;
  code: string;
  scope: string;
  state: string;
}

interface OAuth2CallbackResponse {
  tokens: {
    access_token: string;
    expires_at: number;
    expires_in: number;
    refresh_token: string;
    refresh_token_expires_in: number;
    scope: string;
    token_type: string;
  };
}

import { useApi } from "./api";

export function useIntegrationsService() {
  const api = useApi();

  return {
    /**
     * Get all available integrations
     */
    async getIntegrations(): Promise<Integration[]> {
      const response = await api.get<IntegrationsResponse>(
        "/bff/integrations/"
      );
      return response.connections;
    },

    /**
     * Get all available connections
     */
    async getConnections(): Promise<Connection[]> {
      return api.get<Connection[]>("/bff/connections/");
    },

    /**
     * Get a specific connection by ID
     */
    async getConnection(connectionId: string): Promise<Connection> {
      return api.get<Connection>(`/bff/connections/${connectionId}`);
    },

    /**
     * Create a new connection
     */
    async createConnection(data: CreateConnectionRequest): Promise<Connection> {
      // Convert to a format the API expects
      const requestData = {
        name: data.name,
        service: data.service,
        configuration: data.configuration,
      };
      return api.post<Connection>("/bff/connections/", requestData);
    },

    /**
     * Update an existing connection
     */
    async updateConnection(
      connectionId: string,
      data: UpdateConnectionRequest
    ): Promise<Connection> {
      // Convert to a format the API expects
      const requestData = {
        name: data.name,
        service: data.service,
        configuration: data.configuration,
      };
      return api.put<Connection>(
        `/bff/connections/${connectionId}`,
        requestData
      );
    },

    /**
     * Delete a connection
     */
    async deleteConnection(connectionId: string): Promise<void> {
      return api.delete(`/bff/connections/${connectionId}`);
    },

    /**
     * Get the service catalog
     */
    async getServiceCatalog(): Promise<ServiceCatalog> {
      return api.get<ServiceCatalog>("/bff/connections/catalog");
    },

    /**
     * Generate auth URL for a service credential
     */
    async generateAuthUrl(
      serviceCredentialId: string
    ): Promise<ConnectResponse> {
      return api.get<ConnectResponse>(
        `/bff/connections/${serviceCredentialId}/auth`
      );
    },

    /**
     * Complete the OAuth callback process
     */
    async completeOAuthCallback(
      serviceCredentialId: string,
      name: string,
      queryParams: string,
      integrationId?: string
    ): Promise<CompleteConnectResponse> {
      const url = `/bff/connections/${serviceCredentialId}/continue?name=${encodeURIComponent(
        name
      )}${
        integrationId
          ? `&integration_id=${encodeURIComponent(integrationId)}`
          : ""
      }&${queryParams}`;

      return api.get<CompleteConnectResponse>(url);
    },

    /**
     * Delete an integration
     */
    async deleteIntegration(
      integrationId: string
    ): Promise<DisconnectResponse> {
      return api.delete<DisconnectResponse>(
        `/bff/integrations/${integrationId}`
      );
    },

    /**
     * Initiate OAuth2 connection flow
     */
    async initiateOAuth2Connection(
      data: OAuth2ConnectRequest
    ): Promise<OAuth2ConnectResponse> {
      return api.post<OAuth2ConnectResponse>(
        "/bff/connections/oauth2/connect",
        data
      );
    },

    /**
     * Complete OAuth2 callback process
     */
    async completeOAuth2Callback(
      data: OAuth2CallbackRequest
    ): Promise<OAuth2CallbackResponse> {
      return api.post<OAuth2CallbackResponse>(
        "/bff/connections/oauth2/callback",
        data
      );
    },
  };
}
